import { Link } from 'react-router-dom';
import { useReport } from '../reporting/ReportContext';
import { FilterBar } from '../reporting/FilterBar';
import { PageHead } from '../reporting/PageHead';
import { KpiCard, Panel } from '../dashboard/ui';
import { MultiLine } from '../dashboard/charts';
import { CHANNEL_COLORS } from '../dashboard/colors';
import { km, num, pct, periodDelta, shortDate } from '../lib/reporting';

const PRIMARY = CHANNEL_COLORS.NVS;

export function Overview() {
  const { summary, prevSummary, error, loading } = useReport();

  const exceptions = summary ? summary.totals.failed + summary.totals.cancelled : 0;
  const prevExceptions = prevSummary ? prevSummary.totals.failed + prevSummary.totals.cancelled : null;

  return (
    <section className="report">
      <PageHead
        crumb="Reporting / Overview"
        title="Overview"
        subtitle={
          summary
            ? `NVS in-house · ${summary.from} → ${summary.to} · at-a-glance delivery health. DPD handoffs excluded.`
            : 'NVS in-house · at-a-glance delivery health'
        }
      />
      <FilterBar />

      {error && <p className="form-error" role="alert">{error}</p>}
      {!summary && !error && <p className="muted">Loading…</p>}

      {summary && (
        <>
          <div className="kpi-grid" aria-busy={loading}>
            <KpiCard
              label="Stops"
              value={num(summary.totals.stops)}
              sub={`${num(summary.totals.drivers)} active drivers`}
              accent={PRIMARY}
              delta={periodDelta(summary.totals.stops, prevSummary?.totals.stops, { kind: 'count' })}
            />
            <KpiCard
              label="DIFOT"
              value={pct(summary.rates.difot)}
              sub="Delivered & in full"
              tone={summary.rates.difot >= 0.95 ? 'good' : 'neutral'}
              accent={PRIMARY}
              delta={periodDelta(summary.rates.difot, prevSummary?.rates.difot, { kind: 'rate' })}
            />
            <KpiCard
              label="Exceptions"
              value={num(exceptions)}
              sub="Failed + cancelled"
              tone={exceptions === 0 ? 'good' : 'bad'}
              accent="var(--danger)"
              delta={periodDelta(exceptions, prevExceptions, { kind: 'count', higherIsBetter: false })}
            />
            <KpiCard
              label="Avg distance"
              value={km(summary.totals.avgDistanceKm)}
              sub="Per stop"
              accent={PRIMARY}
            />
          </div>

          <Panel title="Daily volume & DIFOT" subtitle="Stops per day and delivered-in-full rate">
            {summary.daily.length > 0 ? (
              <MultiLine
                categories={summary.daily.map((d) => shortDate(d.date))}
                series={[{ name: 'Stops', color: PRIMARY, points: summary.daily.map((d) => d.stops) }]}
                format={(v) => num(v)}
              />
            ) : (
              <p className="muted">No stops in this range.</p>
            )}
          </Panel>

          <Panel
            title="Top regions by volume"
            subtitle="Busiest depots / regions in range"
            action={<Link to="/reporting/difot" className="panel-link">DIFOT detail →</Link>}
          >
            <div className="data-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Region</th>
                    <th className="num">Stops</th>
                    <th className="num">DIFOT</th>
                    <th className="num">Avg distance</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byRegion.slice(0, 8).map((r) => (
                    <tr key={r.region}>
                      <td>{r.region}</td>
                      <td className="num">{num(r.stops)}</td>
                      <td className="num">{pct(r.difotRate, 1)}</td>
                      <td className="num">{km(r.avgKm)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </section>
  );
}
